import { useCallback, useEffect, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useRecoilState, useResetRecoilState } from 'recoil'

import TenantService from '../../services/TenantService'
import urls, { CARRIER_HOME_PAGE, CARRIER_URLS } from '../../routers/urls'
import { LanguageHelper, LoginHelper, UrlHelper } from '../../utils/helpers'
import { reDirectionAtom } from '../../recoil/atoms'

enum OnboardingPageInfo {
  COMPANY_INFO = 'COMPANY_INFO',
  EMAIL_INFO = 'EMAIL_INFO',
  EMAIL_VERIFY = 'EMAIL_VERIFY',
  HOME_PAGE = 'HOME_PAGE',
  PERSONAL_INFO = 'PERSONAL_INFO',
  PHONE_NUMBER_VERIFY = 'PHONE_NUMBER_VERIFY',
}

type UseOnboardingNextPage = {
  checkAuth: boolean
}

const loginPages = {
  tr: urls.PhoneNumberLogin,
  de: urls.EmailLogin,
  en: urls.EmailLogin,
}

const useOnboardingNextPage = ({ checkAuth }: UseOnboardingNextPage = { checkAuth: true }) => {
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const { data: currentUser, error } = TenantService.getCurrentUser()
  const [reDirectionState, setReDirectionState] = useRecoilState(reDirectionAtom)
  const resetReDirectionState = useResetRecoilState(reDirectionAtom)
  const pageIsCarrierPath = useMemo(() => {
    return Object.values(CARRIER_URLS)
      ?.map?.(item => UrlHelper.getFirstPathItem(item))
      .includes(UrlHelper.getFirstPathItem(pathname))
  }, [])

  const isUnauthorized = error?.response?.data?.data?.errors?.some?.(
    (error: { errorCode: string }) => error?.errorCode === 'UNAUTHORIZED'
  )

  const reDirectionAction = () => {
    if (reDirectionState?.tripLegId) {
      return urls.TripLeg.replace(':id', reDirectionState?.tripLegId)
    }

    if (reDirectionState?.loadId) {
      return urls.Load.replace(':id', reDirectionState?.loadId)
    }

    if (reDirectionState?.pathname) {
      return reDirectionState?.pathname
    }
  }

  const nextPageInfo = useMemo(() => {
    const { onboardingPageInfo } = currentUser?.data || {}
    return onboardingPageInfo || ''
  }, [currentUser])

  const nextPageAction = useCallback(() => {
    if (!nextPageInfo) {
      return
    }

    if (nextPageInfo === OnboardingPageInfo.PERSONAL_INFO) {
      return navigate(urls.UserName)
    }

    if (nextPageInfo === OnboardingPageInfo.COMPANY_INFO) {
      return navigate(urls.CompanyTitle)
    }

    if (nextPageInfo === OnboardingPageInfo.EMAIL_INFO) {
      return navigate(urls.EmailNewContact)
    }

    if (nextPageInfo === OnboardingPageInfo.EMAIL_VERIFY) {
      return navigate(urls.EmailVerificationNewContact)
    }

    if (reDirectionState?.loadId || reDirectionState?.tripLegId || reDirectionState?.pathname) {
      resetReDirectionState()
      return navigate(reDirectionAction() as string)
    }

    if (pageIsCarrierPath) {
      return
    }

    if (nextPageInfo === OnboardingPageInfo.PHONE_NUMBER_VERIFY) {
      return navigate(urls.CarrierHome)
    }

    if (nextPageInfo === OnboardingPageInfo.HOME_PAGE) {
      return navigate(urls.CarrierHome)
    }
  }, [nextPageInfo])

  useEffect(() => {
    if (!!checkAuth && typeof isUnauthorized === 'boolean' && !!isUnauthorized) {
      navigate(loginPages?.[LanguageHelper?.get() as keyof typeof loginPages])
    }
  }, [isUnauthorized])

  useEffect(() => {
    if (!checkAuth) {
      return
    }

    const { tripLegId, id, type } = LoginHelper.getAuthUrlDetails(search)

    setReDirectionState(prev => ({
      ...prev,
      loadId: id,
      ...(!prev?.pathname ? { pathname: pageIsCarrierPath ? pathname : '' } : {}),
      tripLegId,
      type,
    }))
  }, [])

  return [nextPageInfo, nextPageAction, reDirectionAction?.() || CARRIER_HOME_PAGE]
}

export default useOnboardingNextPage
